// ===========================================
// components/SearchBar.js
// ===========================================
import React, { useState, useCallback, useEffect } from 'react';
import { Search, X, Book, Clock, Filter, ChevronDown } from 'lucide-react';
import { useDebounce } from '../hooks/useDebounce';

const SearchBar = ({ books, onSearch, searchStore }) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const debouncedSearchTerm = useDebounce(searchStore.searchTerm, 300);

  const generateSuggestions = useCallback((term) => {
    if (!term.trim()) {
      searchStore.setSearchSuggestions([]);
      return;
    }

    const suggestions = new Set();
    const lowerTerm = term.toLowerCase();

    books.forEach(book => {
      if (book.title.toLowerCase().includes(lowerTerm)) {
        suggestions.add(book.title);
      }
      if (book.author?.toLowerCase().includes(lowerTerm)) {
        suggestions.add(book.author);
      }
    });

    searchStore.setSearchSuggestions(Array.from(suggestions).slice(0, 5));
  }, [books, searchStore]);

  useEffect(() => {
    if (debouncedSearchTerm !== undefined) {
      onSearch(debouncedSearchTerm);
      generateSuggestions(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, onSearch, generateSuggestions]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (searchStore.searchTerm.trim()) {
        searchStore.addToHistory(searchStore.searchTerm.trim());
      }
      setShowSuggestions(false);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const selectSuggestion = (suggestion) => {
    searchStore.setSearchTerm(suggestion);
    searchStore.addToHistory(suggestion);
    setShowSuggestions(false);
  };

  const clearSearch = () => {
    searchStore.setSearchTerm('');
    onSearch('');
  };

  const clearFilters = () => {
    searchStore.setSelectedFilters({
      author: '',
      uploadDate: '',
      fileSize: ''
    });
    onSearch(searchStore.searchTerm);
  };

  return (
    <div className="relative w-full max-w-2xl mx-auto">
      {/* Main Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          placeholder="Search books by title, author, or keyword..."
          value={searchStore.searchTerm}
          onChange={(e) => searchStore.setSearchTerm(e.target.value)}
          onFocus={() => setShowSuggestions(true)}
          onKeyDown={handleKeyDown}
          className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-xl leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
        />
        
        {searchStore.searchTerm && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <button
              onClick={clearSearch}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>

      {/* Search Suggestions Dropdown */}
      {showSuggestions && (searchStore.searchSuggestions.length > 0 || searchStore.searchHistory.length > 0) && (
        <>
          <div className="search-dropdown mt-2">
            {/* Search Suggestions */}
            {searchStore.searchSuggestions.length > 0 && (
              <div className="p-2">
                <div className="text-xs font-medium text-gray-500 px-3 py-2">
                  Suggestions
                </div>
                {searchStore.searchSuggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => selectSuggestion(suggestion)}
                    className="search-suggestion w-full text-left rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      <Book className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-900">{suggestion}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Search History */}
            {searchStore.searchHistory.length > 0 && (
              <div className="p-2 border-t border-gray-200">
                <div className="flex items-center justify-between px-3 py-2">
                  <div className="text-xs font-medium text-gray-500">
                    Recent Searches
                  </div>
                  <button
                    onClick={searchStore.clearSearchHistory}
                    className="text-xs text-gray-400 hover:text-gray-600"
                  >
                    Clear
                  </button>
                </div>
                {searchStore.searchHistory.map((term, index) => (
                  <button
                    key={index}
                    onClick={() => selectSuggestion(term)}
                    className="search-suggestion w-full text-left rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-900">{term}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Click outside to close */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowSuggestions(false)}
          ></div>
        </>
      )}

      {/* Advanced Filters */}
      {showFilters && (
        <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Author Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Author
              </label>
              <input
                type="text"
                placeholder="Filter by author"
                value={searchStore.selectedFilters.author}
                onChange={(e) => searchStore.setSelectedFilters({
                  ...searchStore.selectedFilters,
                  author: e.target.value
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>

            {/* Upload Date Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Upload Date
              </label>
              <select
                value={searchStore.selectedFilters.uploadDate}
                onChange={(e) => searchStore.setSelectedFilters({
                  ...searchStore.selectedFilters,
                  uploadDate: e.target.value
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="">Any time</option>
                <option value="today">Today</option>
                <option value="week">This week</option>
                <option value="month">This month</option>
                <option value="year">This year</option>
              </select>
            </div>

            {/* File Size Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                File Size
              </label>
              <select
                value={searchStore.selectedFilters.fileSize}
                onChange={(e) => searchStore.setSelectedFilters({
                  ...searchStore.selectedFilters,
                  fileSize: e.target.value
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="">Any size</option>
                <option value="small">Small (&lt; 10MB)</option>
                <option value="medium">Medium (10-50MB)</option>
                <option value="large">Large (&gt; 50MB)</option>
              </select>
            </div>

            {/* Clear Filters */}
            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toggle Filters Button */}
      <div className="mt-3 flex justify-center">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors"
        >
          <Filter className="h-4 w-4" />
          {showFilters ? 'Hide Filters' : 'Show Filters'}
          <ChevronDown className={`h-4 w-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
        </button>
      </div>
    </div>
  );
};

export default SearchBar;